<template>
  <div>
    <Loading :active.sync="loading" :isFullPage="false"></Loading>

    <!-- Label Dialog (PDF result — stays as dialog) -->
    <Dialog
      header="Etiqueta NACEX"
      :visible.sync="showLabelDialog"
      :style="{ width: '50vw' }"
      :modal="true"
    >
      <div class="p-text-center" style="text-align: center;">
        <p class="p-mb-3" style="font-size: 1.1rem;">
          <i class="pi pi-check-circle" style="color: green; font-size: 1.5rem;"></i>
          Envio NACEX criado com sucesso!
        </p>
        <div v-if="labelPdfData">
          <Button label="Imprimir Etiqueta" icon="pi pi-print" @click="printLabel" class="p-mb-3" />
          <br />
          <iframe :src="labelPdfData" width="100%" height="450px" style="border: 1px solid #ccc;"></iframe>
        </div>
      </div>
      <template #footer>
        <Button label="Fechar" icon="pi pi-times" class="p-button-secondary" @click="showLabelDialog = false; labelPdfData = null" />
      </template>
    </Dialog>

    <!-- Confirm Delete Dialog -->
    <ConfirmDialog />

    <!-- List view -->
    <Card v-if="!showForm" class="p-no-shadow-app">
      <template #title>
        <div class="p-d-flex p-jc-between p-ai-center">
          <span>Envio</span>
          <Button
            v-if="canEdit"
            label="Novo Envio"
            icon="pi pi-plus"
            class="p-button-sm p-button-success"
            @click="openNewForm"
          />
        </div>
      </template>
      <template #content>
        <DataTable
          :value="envios"
          :loading="loadingTable"
          responsiveLayout="scroll"
          class="p-datatable-sm"
          :autoLayout="true"
          :expandedRows.sync="expandedRows"
          @row-expand="onRowExpand"
          @row-collapse="onRowCollapse"
        >
          <Column :expander="true" style="width: 3rem" />
          <Column field="shippingDate" header="Data" />
          <Column field="trackingNumber" header="Nº Guia" />
          <Column field="shippingMethod" header="Envio">
            <template #body="slotProps">
              <span v-if="slotProps.data.nacex" class="p-ml-2 nacex-badge">NACEX</span>
              <span v-else>{{ slotProps.data.shippingMethod }}</span>
            </template>
          </Column>
          <Column header="Estado" v-if="hasNacexRecords">
            <template #body="slotProps">
              <span v-if="slotProps.data.nacex" :class="'status-badge status-' + getStatusClass(slotProps.data.nacex.status)">
                {{ slotProps.data.nacex.status }}
              </span>
            </template>
          </Column>
          <Column field="scheduledByName" header="Marcado por" />
          <Column header="Ações" style="width: 220px">
            <template #body="slotProps">
              <Button
                icon="pi pi-pencil"
                class="p-button-rounded p-button-success p-button-text"
                v-tooltip.top="'Editar'"
                @click="openEditForm(slotProps.data)"
              />
              <Button
                v-if="slotProps.data.nacex && slotProps.data.nacex.trackingUrl"
                icon="pi pi-external-link"
                class="p-button-rounded p-button-info p-button-text"
                v-tooltip.top="'Rastrear na NACEX'"
                @click="openTracking(slotProps.data.nacex.trackingUrl)"
              />
              <Button
                v-if="slotProps.data.nacex"
                icon="pi pi-sync"
                class="p-button-rounded p-button-info p-button-text"
                v-tooltip.top="'Sincronizar NACEX'"
                @click="syncNacexStatus(slotProps.data)"
              />
              <Button
                v-if="slotProps.data.nacex && slotProps.data.nacex.labelBase64"
                icon="pi pi-print"
                class="p-button-rounded p-button-warning p-button-text"
                v-tooltip.top="'Etiqueta'"
                @click="downloadLabel(slotProps.data)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-danger p-button-text"
                v-tooltip.top="'Eliminar'"
                @click="confirmDelete(slotProps.data)"
              />
            </template>
          </Column>
          <template #expansion="slotProps">
            <div v-if="slotProps.data.nacex">
              <div v-if="slotProps.data.loadingDetails" class="p-text-center p-p-3">
                <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
                <p class="p-mt-2">A carregar detalhes NACEX...</p>
              </div>
              <div v-else-if="slotProps.data.nacexDetails" class="expanded-row-content">
                <div class="p-grid p-mb-2">
                  <div class="p-col-12 p-md-6">
                    <h5 class="text-primary p-mb-2">
                      <i class="pi pi-building p-mr-2"></i>Remetente
                    </h5>
                    <div class="info-card" v-if="slotProps.data.nacexDetails.sender">
                      <strong>{{ slotProps.data.nacexDetails.sender.name }}</strong><br>
                      {{ slotProps.data.nacexDetails.sender.address }}<br>
                      {{ slotProps.data.nacexDetails.sender.zip }} {{ slotProps.data.nacexDetails.sender.city }}<br>
                      <small v-if="slotProps.data.nacexDetails.sender.phone">
                        <i class="pi pi-phone"></i> {{ slotProps.data.nacexDetails.sender.phone }}
                      </small>
                    </div>
                  </div>
                  <div class="p-col-12 p-md-6">
                    <h5 class="text-primary p-mb-2">
                      <i class="pi pi-home p-mr-2"></i>Destinatário
                    </h5>
                    <div class="info-card" v-if="slotProps.data.nacexDetails.recipient">
                      <strong>{{ slotProps.data.nacexDetails.recipient.name }}</strong><br>
                      {{ slotProps.data.nacexDetails.recipient.address }}<br>
                      {{ slotProps.data.nacexDetails.recipient.zip }} {{ slotProps.data.nacexDetails.recipient.city }}<br>
                      <small v-if="slotProps.data.nacexDetails.recipient.phone">
                        <i class="pi pi-phone"></i> {{ slotProps.data.nacexDetails.recipient.phone }}
                      </small>
                    </div>
                  </div>
                </div>
                <div class="p-grid p-mb-2">
                  <div class="p-col-12">
                    <div class="info-card">
                      <strong>Nº NACEX:</strong> {{ slotProps.data.nacex.shipmentCode }}<br>
                      <strong>Estado:</strong> {{ slotProps.data.nacex.status }}<br>
                      <template v-if="slotProps.data.nacexDetails.packages">
                        <strong>Volumes:</strong> {{ slotProps.data.nacexDetails.packages }}
                        <span v-if="slotProps.data.nacexDetails.weight"> ({{ slotProps.data.nacexDetails.weight }} kg)</span><br>
                      </template>
                      <template v-if="slotProps.data.nacexDetails.service">
                        <strong>Serviço:</strong> {{ slotProps.data.nacexDetails.service }}<br>
                      </template>
                    </div>
                  </div>
                </div>
                <div v-if="slotProps.data.nacexDetails.history && slotProps.data.nacexDetails.history.length > 0">
                  <h5 class="p-mb-2"><i class="pi pi-list p-mr-2"></i>Histórico de Estados</h5>
                  <DataTable :value="slotProps.data.nacexDetails.history" class="p-datatable-sm history-table">
                    <Column field="datetime" header="Data / Hora" />
                    <Column field="status" header="Estado">
                      <template #body="h">
                        <span style="font-weight: 600;">{{ h.data.status }}</span>
                      </template>
                    </Column>
                    <Column field="details" header="Detalhes" />
                    <Column field="agency" header="Agência" />
                  </DataTable>
                </div>
              </div>
              <div v-else class="p-text-center p-p-3" style="color: #ff9800;">
                <i class="pi pi-exclamation-circle p-mr-2"></i>Não foi possível carregar os detalhes NACEX.
              </div>
            </div>
            <div v-else class="expanded-row-content">
              <div class="p-mb-3" style="color: #6c757d; font-size: 0.95rem;">
                <i class="pi pi-info-circle p-mr-2"></i>Este envio não está ligado a uma operação NACEX.
              </div>
              <h5 class="text-primary p-mb-3">
                <i class="pi pi-envelope p-mr-2"></i>Detalhes do Envio
              </h5>
              <div class="p-grid p-formgrid p-mb-3">
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Meio de Envio:</strong> {{ slotProps.data.shippingMethod || '—' }}<br>
                    <strong>Data de Envio:</strong> {{ slotProps.data.shippingDate || '—' }}<br>
                    <strong>Técnico:</strong> {{ slotProps.data.scheduledByName || '—' }}<br>
                    <strong>Label / Enviado:</strong> {{ slotProps.data.labelSent ? 'Sim' : 'Não' }}
                  </div>
                </div>
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Guia:</strong> {{ slotProps.data.trackingNumber || '—' }}<br>
                    <strong>Nº Volumes:</strong> {{ slotProps.data.volumeCount || 0 }}<br>
                    <strong>Com Retorno:</strong> {{ slotProps.data.withReturn ? 'Sim' : 'Não' }}<br>
                    <strong>À Cobrança:</strong> {{ slotProps.data.codAmount > 0 ? slotProps.data.codAmountFormatted + ' €' : 'Não' }}
                  </div>
                </div>
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Data de Entrega:</strong> {{ slotProps.data.deliveryDate || '—' }}<br>
                    <strong>Instruções:</strong> {{ slotProps.data.instructions || '—' }}<br>
                    <strong>Observações:</strong> {{ slotProps.data.shippingNotes || '—' }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>

    <!-- Inline Form -->
    <Card v-if="showForm">
      <template #title>
        <div class="p-d-flex p-jc-between p-ai-center">
          <span>{{ editingId ? 'Editar Envio' : 'Novo Envio' }}</span>
          <Button
            icon="pi pi-arrow-left"
            label="Voltar"
            class="p-button-sm p-button-text"
            @click="closeForm"
          />
        </div>
      </template>
      <template #content>
        <div>
          <div class="p-fluid">
            <div class="p-grid p-formgrid">
              <div class="p-field p-col-12 p-md-4">
                <label>Meio de Envio *</label>
                <Dropdown
                  :disabled="editingId !== null"
                  v-model="form.shippingMethod"
                  :options="meiosEnvio"
                  optionLabel="label"
                  optionValue="value"
                  class="p-inputtext-sm w-full"
                  appendTo="body"
                  placeholder="Selecione"
                />
              </div>

              <div class="p-field p-col-12 p-md-3" v-if="!isNacex">
                <label>Data de Envio</label>
                <Calendar
                  v-model="form.shippingDate"
                  dateFormat="dd/mm/yy"
                  :showIcon="true"
                  :disabledDays="[0,6]"
                  inputClass="p-inputtext-sm"
                  class="w-full"
                />
              </div>

              <div class="p-field p-col-12 p-md-2 p-d-flex p-ai-center p-mt-2" v-if="!isNacex">
                <Checkbox v-model="form.labelSent" :binary="true" />
                <label class="p-ml-2">LABEL / Enviado</label>
              </div>

              <div class="p-field p-col-12 p-md-2 p-d-flex p-ai-end p-pb-2" v-if="!isNacex">
                <Checkbox v-model="form.withReturn" :binary="true" />
                <label class="p-ml-2">C/ Retorno?</label>
              </div>

              <div class="p-field p-col-12 p-md-3" v-if="!isNacex">
                <label>Guia</label>
                <InputText v-model="form.trackingNumber" class="p-inputtext-sm" />
              </div>

              <div class="p-field p-col-12 p-md-2" v-if="!isNacex">
                <label>Nº Volumes</label>
                <InputNumber
                  v-model="form.volumeCount"
                  :min="0"
                  class="p-inputtext-sm w-full"
                />
              </div>

              <div class="p-field p-col-12 p-md-2" v-if="!isNacex">
                <label>À Cobrança</label>
                <InputNumber
                  v-model="form.codAmount"
                  mode="decimal"
                  :minFractionDigits="2"
                  :maxFractionDigits="2"
                  class="p-inputtext-sm w-full"
                />
              </div>

              <div class="p-field p-col-12 p-md-6" v-if="!isNacex">
                <label>OBS de Envio</label>
                <Textarea v-model="form.shippingNotes" rows="3" class="p-inputtext-sm w-full" />
              </div>

              <div class="p-field p-col-12 p-md-6" v-if="!isNacex">
                <label>Instruções</label>
                <Textarea v-model="form.instructions" rows="3" class="p-inputtext-sm w-full" />
              </div>

              <div class="p-field p-col-12 p-md-3" v-if="!isNacex">
                <label>Data entrega</label>
                <Calendar
                  v-model="form.deliveryDate"
                  dateFormat="dd/mm/yy"
                  :showIcon="true"
                  :disabledDays="[0,6]"
                  inputClass="p-inputtext-sm"
                  class="w-full"
                />
              </div>

              <div class="p-field p-col-12 p-md-3">
                <label>Técnico</label>
                <Dropdown
                  v-model="form.scheduledBy"
                  :options="technicians"
                  optionLabel="username"
                  optionValue="user_id"
                  class="p-inputtext-sm w-full"
                  appendTo="body"
                  placeholder="Selecione"
                />
              </div>

            </div>

            <NacexInlineFields
              v-if="isNacex && !editingId"
              ref="nacexFields"
              v-model="nacexData"
              type="shipment"
              :patData="patData"
            />
            <div v-if="isNacex && editingId" class="p-p-3 p-text-center" style="color: #6c757d;">
              <i class="pi pi-info-circle p-mr-2"></i>
              Campos NACEX não editáveis após criação.
            </div>
          </div>
          <div class="p-d-flex p-jc-end p-mt-4">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              class="p-button-text p-mr-2"
              @click="closeForm"
            />
            <Button
              :label="editingId ? 'Atualizar' : 'Registar Envio'"
              icon="pi pi-check"
              class="p-button-success"
              :loading="saving"
              @click="saveEnvio"
            />
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import PatService from "../../services/pat.service";
import ShipmentsService from "../../services/shipments.service";
import { TRANSPORTADORAS } from "../../constants/pat-options";
import NacexInlineFields from "../shipment/NacexInlineFields.vue";
const emptyForm = (currentUser = null) => ({
  shippingMethod: 'NACEX',
  shippingDate: new Date(),
  labelSent: false,
  trackingNumber: "",
  volumeCount: 0,
  withReturn: false,
  codAmount: 0,
  shippingNotes: "",
  instructions: "",
  deliveryDate: null,
  scheduledBy: currentUser ? currentUser.id : null,
});

export default {
  name: "PatShipment",
  components: {
    Loading,
    NacexInlineFields,
  },
  props: {
    patId: { type: Number, required: true },
    patInfo: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      loadingTable: false,
      saving: false,
      showForm: false,
      editingId: null,
      expandedRows: [],
      technicians: [],
      form: emptyForm(this.$store.state.auth.user),
      meiosEnvio: TRANSPORTADORAS,
      envios: [],
      nacexData: {},
      showLabelDialog: false,
      labelPdfData: null,
    };
  },
  computed: {
    canEdit() {
      return this.can("managePATs");
    },
    isNacex() {
      return this.form.shippingMethod === "NACEX";
    },
    hasNacexRecords() {
      return this.envios.some((e) => e.nacex);
    },
    patData() {
      return this.buildPatData();
    },
  },
  async mounted() {
    this.loading = true;
    await this.loadData();
    this.loading = false;
  },
  methods: {
    getStatusClass(status) {
      const normalized = (status || '').toString().trim().toUpperCase();

      if (['CREATED', 'SCHEDULED', 'PENDIENTE', 'NOTIFICADO', 'RECOGIDO'].includes(normalized)) {
          return 'info';
      }

      if ([
          'EN TRANSITO',
          'TRANSITO',
          'EN REPARTO',
          'REPARTO',
          'ASIGNADA A MENSAJERO PARA RECOGER',
          'DISP.P.E.'
      ].includes(normalized)) {
          return 'warning';
      }

      if (['ENTREGADO', 'OK', 'RECOGIDA'].includes(normalized)) {
          return 'success';
      }

      if ([
          'CANCELED',
          'ANULADO',
          'ANULADA',
          'INCIDENCIA',
          'SOL SIN OK',
          'RECHAZADA'
      ].includes(normalized)) {
          return 'danger';
      }
      return 'secondary';
    },
    async loadData() {
      try {
        this.loadingTable = true;
        const [data, techs] = await Promise.all([
          PatService.getDispatches(this.patId),
          PatService.getTechnicians(),
        ]);

        if (techs) this.technicians = techs;
        this.envios = data?.data || data || [];
      } catch (err) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Erro ao carregar envios.", life: 3000 });
      } finally {
        this.loadingTable = false;
      }
    },
    openNewForm() {
      this.form = emptyForm(this.$store.state.auth.user);
      this.nacexData = {};
      this.editingId = null;
      this.showForm = true;
    },
    openEditForm(row) {
      this.form = {
        shippingMethod: row.shippingMethod,
        shippingDate: row.shippingDate ? new Date(row.shippingDate.split('-').reverse().join('-')) : null,
        labelSent: row.labelSent || false,
        trackingNumber: row.trackingNumber || "",
        volumeCount: row.volumeCount || 0,
        withReturn: row.withReturn || false,
        codAmount: row.codAmount || 0,
        shippingNotes: row.shippingNotes || "",
        instructions: row.instructions || "",
        deliveryDate: row.deliveryDate ? new Date(row.deliveryDate.split('-').reverse().join('-')) : null,
        scheduledBy: row.scheduledBy,
      };
      this.editingId = row.id;
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
      this.editingId = null;
      this.form = emptyForm(this.$store.state.auth.user);
      this.nacexData = {};
    },
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    buildPayload() {
      const payload = {
        shipping_method: this.form.shippingMethod,
        shipping_date: this.formatDate(this.form.shippingDate),
        label_sent: this.form.labelSent,
        tracking_number: this.form.trackingNumber,
        volume_count: this.form.volumeCount,
        with_return: this.form.withReturn,
        cod_amount: this.form.codAmount,
        shipping_notes: this.form.shippingNotes,
        instructions: this.form.instructions,
        delivery_date: this.formatDate(this.form.deliveryDate),
        scheduled_by: this.form.scheduledBy,
      };

      if (this.isNacex && !this.editingId && this.$refs.nacexFields) {
        Object.assign(payload, this.$refs.nacexFields.getNacexPayload());
      }

      return payload;
    },
    async saveEnvio() {
      this.saving = true;
      try {
        let result;
        const isEdit = !!this.editingId;
        const payload = this.buildPayload();

        if (isEdit) {
          result = await PatService.updateDispatch(this.patId, this.editingId, payload);
        } else {
          result = await PatService.createDispatch(this.patId, payload);
        }

        // Show label dialog if NACEX created with label
        if (!isEdit && this.isNacex && result?.data?.nacex?.labelBase64) {
          this.labelPdfData = `data:application/pdf;base64,${result.data.nacex.labelBase64}`;
          this.showLabelDialog = true;
        }

        this.closeForm();
        await this.loadData();
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: isEdit ? "Envio atualizado." : "Envio criado com sucesso.",
          life: 3000,
        });
      } catch (err) {
        const msg = err.response?.data?.details || err.response?.data?.error || "Erro ao guardar envio.";
        this.$toast.add({ severity: "error", summary: "Erro", detail: msg, life: 5000 });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(row) {
      this.$confirm.require({
        message: row.nacex
          ? "Este envio está ligado a uma operação NACEX e será cancelado. Tem a certeza?"
          : "Tem a certeza que pretende eliminar este envio?",
        header: "Confirmar eliminação",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        acceptLabel: "Eliminar",
        rejectLabel: "Cancelar",
        accept: () => this.deleteEnvio(row),
      });
    },
    async deleteEnvio(row) {
      try {
        this.loading = true;
        await PatService.deleteDispatch(this.patId, row.id);
        this.$toast.add({ severity: "success", summary: "Eliminado", detail: "Envio eliminado.", life: 3000 });
        await this.loadData();
      } catch (err) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Erro ao eliminar envio.", life: 3000 });
      } finally {
        this.loading = false;
      }
    },
    async onRowExpand(event) {
      const row = event.data;
      if (!row.nacex) return;
      this.$set(row, 'loadingDetails', true);
      this.$set(row, 'nacexDetails', null);
      this.expandedRows = [row];
      try {
        const response = await ShipmentsService.getShipmentById(row.nacex.id);
        if (response && response.data && response.data.data && response.data.data.nacex_details) {
          this.$set(row, 'nacexDetails', response.data.data.nacex_details);
        } else if (response && response.data && response.data.nacex_details) {
          this.$set(row, 'nacexDetails', response.data.nacex_details);
        }
      } catch (err) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Não foi possível carregar detalhes NACEX.", life: 3000 });
      } finally {
        this.$set(row, 'loadingDetails', false);
      }
    },
    onRowCollapse(event) {
      this.expandedRows = this.expandedRows.filter((r) => r.id !== event.data.id);
    },
    openTracking(url) {
      if (url) window.open(url, '_blank');
    },
    async syncNacexStatus(row) {
      try {
        const result = await PatService.syncDispatchStatus(this.patId, row.id);
        await this.loadData();
        this.$toast.add({ severity: "success", summary: "Sincronizado", detail: `Estado: ${result.status}`, life: 3000 });
      } catch (err) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Erro ao sincronizar.", life: 3000 });
      }
    },
    downloadLabel(row) {
      if (!row.nacex?.labelBase64) return;
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${row.nacex.labelBase64}`;
      link.download = `Nacex_Envio_${row.nacex.shipmentCode || row.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    printLabel() {
      if (!this.labelPdfData) return;
      const link = document.createElement("a");
      link.href = this.labelPdfData;
      link.download = "Nacex_Envio.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    statusBadgeClass(status) {
      if (status === 'DELIVERED' || status === 'COMPLETED') return 'badge badge-success';
      if (status === 'CANCELED') return 'badge badge-danger';
      if (status === 'CREATED') return 'badge badge-info';
      return 'badge badge-warning';
    },
    buildPatData() {
      if (!this.patInfo) return {};
      const data = {
        reference: String(this.patId),
        client: null,
        entity: null,
        externalRef: this.patInfo.externalRef || '',
        atcodeid: this.patInfo.transportDocumentAtcodeid || '',
      };
      if (this.patInfo.client) data.client = this.parseContactData(this.patInfo.client);
      if (this.patInfo.entity) data.entity = this.parseContactData(this.patInfo.entity);
      return data;
    },
    parseContactData(contact) {
      if (!contact) return null;
      const parsed = {
        name: contact.name || contact.nome || '',
        address: contact.address || contact.morada || '',
        zip: '',
        city: contact.city || contact.local || '',
        phone: contact.mobilPhone || contact.telephone || contact.tlmvl || '',
        email: contact.email || '',
        country: 'PT',
        observations1: '',
        observations2: ''
      };

      const codpost = contact.zip_code || contact.zipCode || contact.postalCode || '';
      if (codpost) {
        const parts = codpost.split(' ');
        parsed.zip = parts[0] || '';
        parsed.city = parts.slice(1).join(' ') || contact.city || contact.local || '';
      }

      return parsed;
    },
  },
};
</script>

<style scoped>
.w-full {
  width: 100%;
}

.nacex-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #2196F3;
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}
.badge-success { background: #c8e6c9; color: #256029; }
.badge-danger { background: #ffcdd2; color: #c63737; }
.badge-info { background: #b3e5fc; color: #23547b; }
.badge-warning { background: #ffd8b2; color: #805b36; }

.expanded-row-content {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin: 0.5rem 0;
  padding: 1rem;
}

.text-primary {
  color: #0056b3;
  font-size: 1rem;
  font-weight: 600;
}

.info-card {
  background: white;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-size: 0.9rem;
  line-height: 1.6;
}

.history-table {
  border: 1px solid #dee2e6;
}
</style>
